// Libs
import React, { useState, useContext, useEffect } from "react";
import { useObserver, KeyValuePair, FieldType, Else } from "@shoothill/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { FormLabel, Box, Typography, Checkbox, FormControlLabel, Radio, FormControl, FormHelperText, Select, InputAdornment, TextField } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";

// App
import { StoresContext, Stores } from "Globals/Stores";

// Models
import { EmploymentStatusEnum } from "Globals/Models/EmploymentStatusEnum";
import { PropertyDetailsModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PropertyDetailsModel";

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";

// Views
import { CurrencyInput } from "Globals/Views/CurrencyInput";
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";
import { CustomDatePicker } from "Globals/Views/CustomDatePicker";

// Styles
import { theme } from "Globals/Styles/AppTheme";
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { CustomTwoSelects } from "Globals/Styles/CustomTwoSelects";
import { Row } from "Globals/Styles/LayoutStyles";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import CurrentPropertyDetailsViewModel from "Globals/ViewModels/FormSections/CurrentPropertyDetails/CurrentPropertyDetailsViewModel";
import { CustomInput } from "Globals/Styles/AppStyling";

export const StyledTextAreaContainer = styled(Box)`
    width: 100%;
`;

interface IProps {
    viewModel: CurrentPropertyDetailsViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const SellingYourHome: React.FC<IProps> = (props: IProps) => {
    const { viewModel, applicationFormViewModel } = props;
    const storesContext = useContext<Stores>(StoresContext);
    return useObserver(() => (
        <>
            <Row>
                <CustomInput
                    label="Property in the name of"
                    placeHolder="Enter property in the name of"
                    viewModel={viewModel}
                    fieldName="propertyOwnerName"
                    shrink={true}
                    onChange={() => viewModel.handleInputChange()}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    label="Property address line 1"
                    placeHolder="Address line 1"
                    viewModel={viewModel}
                    fieldName="addressLine1"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Property address line 2"
                    placeHolder="Address line 2"
                    viewModel={viewModel}
                    fieldName="addressLine2"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Property address line 3"
                    placeHolder="Address line 3"
                    viewModel={viewModel}
                    fieldName="addressLine3"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    label="Property city/town"
                    placeHolder="City/Town"
                    viewModel={viewModel}
                    fieldName="city"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Property county"
                    placeHolder="County"
                    viewModel={viewModel}
                    fieldName="county"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Property postcode"
                    placeHolder="Postcode"
                    viewModel={viewModel}
                    fieldName="postcode"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CurrencyInput
                    onChange={() => viewModel.handleInputChange()}
                    viewModel={viewModel}
                    label={"Amount of mortgage outstanding"}
                    placeholder={"Enter the outstanding amount"}
                    fieldName={"mortgageAmountOutstanding"}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
            </Row>
            <Row>
                <CurrencyInput
                    onChange={() => viewModel.handleInputChange()}
                    viewModel={viewModel}
                    label={"Amount to be received from transfer"}
                    placeholder={"Enter the amount to be received"}
                    fieldName={"transferAmountReceived"}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
            </Row>
            <Row>
                <CustomYesNoRadio
                    label="Are there any arrears or negative equity on the property?"
                    fieldName={"hasNegativeEquity"}
                    value={viewModel.model.hasNegativeEquity}
                    onChange={viewModel.handleRadioChange}
                    disabled={applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("hasNegativeEquity")}
                />
            </Row>
            {viewModel.model.hasNegativeEquity && (
                <Box style={{ backgroundColor: "#F0F0F0", padding: "15px", marginTop: "30px" }}>
                    <Row style={{ marginTop: "0px" }}>
                        <Typography variant="body2" style={{ fontWeight: "bold" }}>
                            How much is outstanding and what are your arrangements to repay?
                        </Typography>
                    </Row>
                    <Row style={{ marginTop: "0px" }}>
                        <StyledTextAreaContainer>
                            <CustomInput
                                onChange={() => viewModel.handleInputChange()}
                                multiLine={true}
                                fullwidth={true}
                                placeHolder="Notes..."
                                viewModel={viewModel}
                                fieldName="negativeEquityNotes"
                                inputProps={{
                                    disabled: props.applicationFormViewModel.model.isSubmitted,
                                }}
                            />
                        </StyledTextAreaContainer>
                    </Row>
                </Box>
            )}
            <Row>
                <CustomYesNoRadio
                    label="Do you have a buyer for your home?"
                    fieldName={"hasBuyerForProperty"}
                    value={viewModel.model.hasBuyerForProperty}
                    onChange={viewModel.handleRadioChange}
                    disabled={applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("hasBuyerForProperty")}
                />
            </Row>
            <Row>
                <CustomYesNoRadio
                    label="Are you selling through an estate agent?"
                    fieldName={"isSellingWithEstateAgent"}
                    value={viewModel.model.isSellingWithEstateAgent}
                    onChange={viewModel.handleRadioChange}
                    disabled={applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("isSellingWithEstateAgent")}
                />
                {viewModel.model.isSellingWithEstateAgent && (
                    <>
                        <CustomInput
                            label="Estate agent name"
                            viewModel={viewModel}
                            fieldName="estateAgentName"
                            placeHolder="Enter estate agent name"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                        <CustomInput
                            label="Estate agent telephone number"
                            viewModel={viewModel}
                            fieldName="estateAgentPhone"
                            placeHolder="Enter telephone number"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </>
                )}
            </Row>
            <Row>
                <CustomDatePicker
                    viewModel={viewModel}
                    label="What is the estimated completion date?"
                    placeholder={"Enter the estimated completion date?"}
                    fieldName="estimatedCompletedDate"
                    value={viewModel.model.estimatedCompletedDate}
                    onChange={(date) => {
                        viewModel.setDate("estimatedCompletedDate", date);
                        viewModel.restartTimer();
                    }}
                    resetCallback={viewModel.restartTimer}
                    disabled={applicationFormViewModel.model.isSubmitted}
                />
            </Row>
            <Row>
                <CustomYesNoRadio
                    label="Is it a shared ownership property?"
                    fieldName={"isSharedOwnership"}
                    value={viewModel.model.isSharedOwnership}
                    onChange={viewModel.handleRadioChange}
                    disabled={applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("isSharedOwnership")}
                />
            </Row>
            {viewModel.model.isSharedOwnership && (
                <Box style={{ backgroundColor: "#F0F0F0", padding: "15px", marginTop: "30px" }}>
                    <Row>
                        <CustomInput
                            type="select"
                            style={{ minWidth: "350px" }}
                            placeHolder={"Please select"}
                            label="What percentage do you own?"
                            viewModel={viewModel}
                            fieldName="percentageOwned"
                            selectItems={storesContext.Domain.SelectItemsStore.getSharedOwnershipOptions}
                            onChange={() => viewModel.handleInputChange()}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    </Row>
                    <Row>
                        <CustomInput
                            label="Housing association name"
                            viewModel={viewModel}
                            fieldName="housingAssociationName"
                            placeHolder="Enter housing association name"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                        <CustomInput
                            label="Housing association telephone number"
                            viewModel={viewModel}
                            fieldName="housingAssociationPhone"
                            placeHolder="Enter association telephone number"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </Row>
                    <Row>
                        <CustomInput
                            label="Housing association address line 1"
                            placeHolder="Address line 1"
                            viewModel={viewModel}
                            fieldName="housingAssociationAddressLine1"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Housing association address line 2"
                            placeHolder="Address line 2"
                            viewModel={viewModel}
                            fieldName="housingAssociationAddressLine2"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Housing association address line 3"
                            placeHolder="Address line 3"
                            viewModel={viewModel}
                            fieldName="housingAssociationAddressLine3"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    </Row>
                    <Row>
                        <CustomInput
                            label="Housing association city/town"
                            placeHolder="City/Town"
                            viewModel={viewModel}
                            fieldName="housingAssociationCity"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Housing association county"
                            placeHolder="County"
                            viewModel={viewModel}
                            fieldName="housingAssociationCounty"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Housing association postcode"
                            placeHolder="Postcode"
                            viewModel={viewModel}
                            fieldName="housingAssociationPostcode"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    </Row>
                    <Row>
                        <Row style={{ marginTop: "0px" }}>
                            <StyledTextAreaContainer>
                                <CustomInput
                                    onChange={() => viewModel.handleInputChange()}
                                    multiLine={true}
                                    fullwidth={true}
                                    placeHolder="Notes..."
                                    viewModel={viewModel}
                                    fieldName="movingReason"
                                    inputProps={{
                                        disabled: props.applicationFormViewModel.model.isSubmitted,
                                    }}
                                />
                            </StyledTextAreaContainer>
                        </Row>
                    </Row>
                </Box>
            )}
        </>
    ));
};
